import React from "react";
import { inject, observer } from "mobx-react";
import _ from "lodash";
import {
  AnchorButton,
  Button,
  Card,
  ControlGroup,
  Elevation,
  H1,
  NumericInput,
  Tag,
  NonIdealState,
  Spinner,
} from "@blueprintjs/core";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { apiCallPost, indianRupee, niceDateTime, printApiUrl } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";

class CashTill extends React.Component {
  componentDidMount() {
    this.loadSites();
  }

  state = {
    sites: [],
    selectedSite: null,
    isLoadingData: false,
    isLoadingSites: false,
    till: null,
    dailyCashCollected: null,
    touched: false,
  };

  loadSites = async () => {
    try {
      this.setState({ isLoadingSites: true });
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllSites`, {});
      const siteNames = response.map((site) => site.name || site.siteName || site);
      this.setState({ sites: siteNames, isLoadingSites: false });
    } catch (err) {
      console.error("Error loading sites:", err);
      // Fallback to empty array
      this.setState({ sites: [], isLoadingSites: false });
    }
  };

  getData = async () => {
    try {
      this.setState({ isLoadingData: true });
      let res = await apiCallPost(`/v3/${this.props.authStore.prefix}/getMyCashTill`, {
        site: this.state.selectedSite,
      });
      this.setState({
        isLoadingData: false,
        till: res.till,
        dailyCashCollected: res.dailyCashCollected,
      });
      cogoToast.success("Cash till data loaded");
    } catch (err) {
      cogoToast.error("Failed to load cash till data");
      this.setState({ isLoadingData: false });
      console.error("Error loading cash till:", err);
    }
  };

  updateData = async () => {
    try {
      this.setState({ isLoadingData: true });
      let res = await apiCallPost(`/v3/${this.props.authStore.prefix}/updateMyCashTill`, {
        _id: this.state.till._id,
        cash: this.state.till.cash,
      });
      this.setState({
        isLoadingData: false,
        till: res.till,
        touched: false,
      });
      cogoToast.success("Cash till updated successfully");
    } catch (err) {
      cogoToast.error("Failed to update cash till");
      this.setState({ isLoadingData: false });
      console.error("Error updating cash till:", err);
    }
  };

  renderHeader = () => {
    return (
      <div style={{ marginBottom: "20px" }}>
        <H1 style={{ color: "var(--text-primary)", margin: "0 0 10px 0" }}>Cash Till Management</H1>
        <p style={{ color: "var(--text-secondary)", margin: "0 0 20px 0" }}>
          Manage cash denominations and verify daily cash collection
        </p>
        {this.state.isLoadingSites ? (
          <NonIdealState icon={<Spinner size={20} />} title="Loading sites..." />
        ) : (
          <ControlGroup>
            <Tag minimal large intent="primary">
              Choose Site
            </Tag>
            <SingleSelectWrapper
              items={this.state.sites}
              onChange={(item) => this.setState({ selectedSite: item })}
              selectedItem={this.state.selectedSite}
            />
            {this.state.selectedSite ? (
              <Button
                text="Get Data"
                outlined
                loading={this.state.isLoadingData}
                onClick={() => this.getData()}
                intent="primary"
              />
            ) : null}
          </ControlGroup>
        )}
      </div>
    );
  };

  renderTill = () => {
    if (!this.state.till) {
      return (
        <NonIdealState
          icon="bank-account"
          title="No Cash Till Data"
          description="Select a site and click 'Get Data' to view cash till information"
        />
      );
    }
    return (
      <div>
        <ControlGroup style={{ marginBottom: "20px" }}>
          <Tag large minimal intent="primary">
            User
          </Tag>
          <Tag large intent="primary">
            {this.state.till.createdBy}
          </Tag>
          <Tag large minimal intent="success">
            Site
          </Tag>
          <Tag large intent="success">
            {this.state.till.site}
          </Tag>
          <Tag large minimal intent="warning">
            Last Updated
          </Tag>
          <Tag large intent="warning">
            {niceDateTime(this.state.till.updatedAt)}
          </Tag>
          {this.state.touched ? (
            <Button
              icon="endorsed"
              intent="success"
              text="Save"
              loading={this.state.isLoadingData}
              onClick={() => this.updateData()}
            />
          ) : null}
          <AnchorButton
            target="_blank"
            icon="print"
            intent="primary"
            outlined
            href={`${printApiUrl}/neo/sx/cashtill/${this.state.till._id}`}
          />
        </ControlGroup>

        <Card
          elevation={Elevation.TWO}
          style={{
            backgroundColor: "var(--bg-surface)",
            padding: "20px",
            marginBottom: "20px",
          }}
        >
          <h3 style={{ color: "var(--text-primary)", margin: "0 0 15px 0" }}>Cash Denominations</h3>
          {/* {this.renderEachDenom("2000")} */}
          {this.renderEachDenom("500")}
          {this.renderEachDenom("200")}
          {this.renderEachDenom("100")}
          {this.renderEachDenom("50")}
          {this.renderEachDenom("20")}
          {this.renderEachDenom("10")}
          {this.renderEachDenom("5")}
          {this.renderEachDenom("2")}
          {this.renderEachDenom("1")}
        </Card>

        {this.renderCheck()}
      </div>
    );
  };

  renderCheck = () => {
    let sum = 0;
    for (var i = 0; i < this.state.till.cash.length; i++) {
      sum = parseInt(sum + Number(this.state.till.cash[i].denom) * this.state.till.cash[i].count);
    }

    const isMatch = this.state.dailyCashCollected === sum;
    const difference = Math.abs(this.state.dailyCashCollected - sum);

    return (
      <Card
        elevation={Elevation.TWO}
        style={{
          backgroundColor: "var(--bg-surface)",
          padding: "20px",
        }}
      >
        <h3 style={{ color: "var(--text-primary)", margin: "0 0 15px 0" }}>Verification Summary</h3>
        <ControlGroup vertical>
          <ControlGroup>
            <Tag minimal large style={{ width: "200px" }}>
              Total Entered
            </Tag>
            <Tag large intent="primary">
              {indianRupee(sum * 100, 0)}
            </Tag>
          </ControlGroup>
          <ControlGroup>
            <Tag minimal large style={{ width: "200px" }}>
              Expected Amount
            </Tag>
            <Tag large intent="primary">
              {indianRupee(this.state.dailyCashCollected * 100, 0)}
            </Tag>
          </ControlGroup>
          {!isMatch && (
            <ControlGroup>
              <Tag minimal large style={{ width: "200px" }}>
                Difference
              </Tag>
              <Tag large intent="danger">
                {indianRupee(difference * 100, 0)}
              </Tag>
            </ControlGroup>
          )}
          <ControlGroup>
            <Tag
              minimal={isMatch}
              intent={isMatch ? "success" : "danger"}
              large
              style={{ width: "100%", justifyContent: "center" }}
            >
              {isMatch ? "✓ Exact Match" : "⚠ Mismatch Detected"}
            </Tag>
          </ControlGroup>
        </ControlGroup>
      </Card>
    );
  };

  renderEachDenom = (denom) => {
    let whichDenomIdx = _.findIndex(this.state.till.cash, (each) => each.denom === denom);
    let whichDenom = this.state.till.cash[whichDenomIdx];
    return (
      <ControlGroup style={{ marginTop: "10px" }}>
        <Tag minimal large intent="primary" style={{ width: "100px", justifyContent: "flex-end" }}>
          ₹ {whichDenom.denom}
        </Tag>
        <Tag minimal large style={{ width: "30px" }}>
          ×
        </Tag>
        <NumericInput
          value={whichDenom.count}
          buttonPosition="none"
          style={{ width: "120px" }}
          onValueChange={(valueAsNumber) => {
            let ss = this.state.till;
            let cc = ss.cash;
            cc[whichDenomIdx].count = valueAsNumber;
            ss.cash = cc;
            this.setState({ till: ss, touched: true });
          }}
        />
        <Tag minimal large style={{ width: "30px" }}>
          =
        </Tag>
        <Tag minimal large intent="success" style={{ minWidth: "120px" }}>
          {indianRupee(Number(whichDenom.denom) * whichDenom.count * 100, 0)}
        </Tag>
      </ControlGroup>
    );
  };

  render() {
    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        <Card
          style={{
            backgroundColor: "var(--bg-surface)",
            padding: "20px",
          }}
          elevation={Elevation.FOUR}
        >
          {this.renderHeader()}
          {this.state.isLoadingData && !this.state.till ? (
            <NonIdealState icon={<Spinner />} title="Loading cash till data..." />
          ) : (
            this.renderTill()
          )}
        </Card>
      </div>
    );
  }
}

export default inject("authStore")(observer(CashTill));
